// src/pages/Users/Galleries/components/GalleryCard.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCompressAlt,
  FaEye,
  FaImage,
  FaPen,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { deleteGallery } from "../../../../services/gallerieServices";
import { toAbsolutePhotoUrl } from "../../../../services/albumPhotoServices";

import EditAlbumModal from "../EditAlbumModal";

import {
  FALLBACK_ALBUM_IMG,
  countPhotos,
  getCoverUrl,
  getOrderedPhotos,
  stripHtml,
  truncateText,
} from "../utils/galleriesUtils";

function onImgError(event) {
  if (event?.currentTarget) {
    event.currentTarget.src = FALLBACK_ALBUM_IMG;
  }
}

export default function GalleryCard({
  gallery,
  userRole,
  isManager,
  onPreview,
  refreshGalleriesList,
}) {
  const navigate = useNavigate();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [purify, setPurify] = useState(null);

  useEffect(() => {
    async function loadPurify() {
      try {
        const mod = await import("dompurify");
        setPurify(() => (mod.default ? mod.default : mod));
      } catch {
        // Non bloquant.
      }
    }

    loadPurify();
  }, []);

  const orderedPhotos = useMemo(() => getOrderedPhotos(gallery), [gallery]);
  const coverUrl = getCoverUrl(gallery);
  const photoCount = countPhotos(gallery);

  const previewThumbs = orderedPhotos.slice(0, 4);

  const safeHtml = useMemo(() => {
    const raw = gallery?.description || "";

    try {
      return purify ? purify.sanitize(raw) : raw;
    } catch {
      return raw;
    }
  }, [gallery?.description, purify]);

  const descriptionPreview = truncateText(safeHtml, 155);

  const handleDeleteGallery = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette galerie ?")) {
      return;
    }

    try {
      await deleteGallery(gallery.id);
      toast.success("Galerie supprimée avec succès.");
      refreshGalleriesList?.();
    } catch (error) {
      console.error("Erreur lors de la suppression de la galerie:", error);
      toast.error("Erreur lors de la suppression de la galerie.");
    }
  };

  return (
    <article className="group overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-900">
      <div className="relative overflow-hidden bg-slate-100 dark:bg-slate-950">
        <button
          type="button"
          onClick={onPreview}
          className="block w-full text-left"
          aria-label={`Prévisualiser ${gallery?.titre || "album"}`}
        >
          <div className="aspect-[16/12] w-full sm:aspect-[16/11]">
            <img
              src={coverUrl}
              alt={gallery?.titre || "Album"}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
              loading="lazy"
              onError={onImgError}
            />
          </div>

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/15 to-transparent" />

          <div className="absolute left-4 top-4">
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-950 shadow-sm">
              <FaImage className="text-orange-500" />
              {photoCount} photo{photoCount > 1 ? "s" : ""}
            </span>
          </div>

          <div className="absolute right-4 top-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition group-hover:scale-105">
              <FaEye />
            </span>
          </div>

          {!gallery?.is_active && (
            <div className="absolute left-4 top-16">
              <span className="inline-flex rounded-full bg-amber-400 px-3 py-1 text-xs font-bold uppercase text-amber-950 shadow-sm">
                Masqué
              </span>
            </div>
          )}

          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="line-clamp-2 text-2xl font-bold leading-tight text-white drop-shadow-sm">
              {gallery?.titre || "Album sans titre"}
            </h3>
          </div>
        </button>
      </div>

      <div className="p-5">
        <div className="min-h-[94px]">
          {descriptionPreview ? (
            <p className="line-clamp-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
              {descriptionPreview}
            </p>
          ) : (
            <p className="text-sm italic leading-7 text-slate-400">
              Pas de description pour cet album.
            </p>
          )}
        </div>

        {previewThumbs.length > 0 && (
          <div className="mt-4 flex gap-2">
            {previewThumbs.map((photo, index) => {
              const thumbUrl =
                toAbsolutePhotoUrl(photo?.photo_url || "") || FALLBACK_ALBUM_IMG;

              return (
                <button
                  key={photo.id || `${gallery.id}-${index}`}
                  type="button"
                  onClick={onPreview}
                  className="h-14 flex-1 overflow-hidden rounded-2xl border border-white bg-slate-100 shadow-sm ring-1 ring-slate-200 dark:border-slate-800 dark:bg-slate-950 dark:ring-slate-700"
                  aria-label="Prévisualiser l’album"
                >
                  <img
                    src={thumbUrl}
                    alt={stripHtml(photo?.description || `Photo ${index + 1}`)}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={onImgError}
                  />
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onPreview}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-orange-600 shadow-sm transition hover:bg-orange-50 active:scale-95 dark:border-slate-700 dark:bg-slate-950 dark:text-orange-300 dark:hover:bg-orange-950/30"
          >
            Prévisualiser
            <FaCompressAlt className="text-xs" />
          </button>

          <button
            type="button"
            onClick={() => navigate(`/galerie-photos/album/${gallery.id}`)}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 active:scale-95 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            Voir l’album
            <FaArrowRight className="text-xs" />
          </button>
        </div>

        {isManager && (
          <div className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-200 pt-3 dark:border-slate-800">
            <button
              type="button"
              onClick={() => setEditModalOpen(true)}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaPen />
              Modifier
            </button>

            <button
              type="button"
              onClick={handleDeleteGallery}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-red-50 px-3 text-xs font-bold text-red-700 transition active:scale-95 dark:bg-red-950/30 dark:text-red-300"
            >
              <FaTrash />
              Supprimer
            </button>
          </div>
        )}
      </div>

      {editModalOpen && (
        <EditAlbumModal
          gallery={gallery}
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          refreshList={refreshGalleriesList}
        />
      )}
    </article>
  );
}